import { useMutation } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';

interface TokenResponse {
  access_token: string;
  refresh_token: string;
}

/**
 * What the backend refused, and who can fix it.
 *
 * The four codes exist because "your practice is not set up" and "you were never
 * invited" look identical from a browser and have completely different owners:
 * the first is Arcadia's to fix, the second the practice administrator's. A
 * single "sign-in failed" would send every one of these to the wrong person.
 */
export function directoryErrorMessage(code: string | undefined, fallback: string): string {
  switch (code) {
    case 'SSO_EMAIL_MISSING':
      return 'Microsoft did not send an email address. Your IT administrator needs to add the "email" optional claim to the MohairNotes app registration.';
    case 'SSO_TENANT_UNKNOWN':
      return 'Your organisation is not set up for Microsoft sign-in yet. Ask Arcadia Perinatal to register your directory.';
    case 'SSO_USER_NOT_INVITED':
      return 'You do not have a MohairNotes account yet. Ask your administrator to invite you, then try again.';
    case 'SSO_WRONG_ORGANIZATION':
      return 'That account does not belong to this organisation.';
    default:
      return fallback;
  }
}

/**
 * Exchange an Entra ID token for this app's own tokens.
 *
 * A different endpoint from `useSocialSignIn`, and no `organizationName`. This
 * path never creates anything: the organization is decided by the token's own
 * tenant id, server-side, and the person must already be a member of it. There
 * is nothing for the client to name, and sending a name would imply otherwise.
 */
export function useDirectorySignIn() {
  return useMutation({
    mutationFn: async ({ idToken }: { idToken: string }) => {
      const { data } = await clientApi.post<TokenResponse>('/api/v1/auth/sso/microsoft', {
        id_token: idToken,
      });
      return data;
    },
  });
}
